import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { Icon } from '@/shared/ui/icon';

export interface BreadcrumbItem {
  label: ReactNode;
  to?: string;
  icon?: ReactNode;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  label?: string;
  className?: string;
  /**
   * When true the final (current) crumb renders as an `<h1>` so the breadcrumb
   * doubles as the page heading. Used by the app shell top bar, which shows the
   * breadcrumb in place of a separate title.
   */
  asPageHeading?: boolean;
  /**
   * When set, prepend a Home icon link to the workspace root before the trail.
   * Matches the chevron-separated home affordance used in the top bar.
   */
  homeTo?: string;
  /** Accessible label for the Home link. Defaults to `'Home'`. */
  homeLabel?: string;
}

const subtleEase = [0.22, 1, 0.36, 1] as const;

const itemVariants = {
  initial: { opacity: 0, x: -6 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 6 },
};

/**
 * Animated breadcrumb. Interactive crumbs are real SPA `<Link>`s; the current
 * crumb is emphasized (and optionally an `<h1>`). Motion respects the global
 * reduced-motion guard.
 */
export function Breadcrumb({
  items,
  label = 'Breadcrumb',
  className,
  asPageHeading = false,
  homeTo,
  homeLabel = 'Home',
}: BreadcrumbProps): ReactNode {
  if (items.length === 0 && !homeTo) {
    return null;
  }

  const hasHome = Boolean(homeTo);

  return (
    <nav aria-label={label} className={cn('inline-flex min-w-0', className)}>
      <ol className="flex flex-wrap items-center gap-1">
        {hasHome ? (
          <li className="flex items-center">
            <Link
              to={homeTo as string}
              aria-label={homeLabel}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-container)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--field-focus-ring)]"
            >
              <Icon name="home" className="h-4 w-4" />
            </Link>
          </li>
        ) : null}
        <AnimatePresence mode="popLayout" initial={false}>
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            const isInteractive = Boolean(item.to) && !isLast;
            const key = item.to ? `${String(item.label)}-${item.to}` : `crumb-${index}`;

            // Render the label directly (no wrapper span) so `aria-current`
            // sits on the element that holds the text — keeps getByText queries
            // and screen readers pointed at the same node.
            const inner = item.icon ? (
              <>
                <span className="inline-flex shrink-0">{item.icon}</span>
                {item.label}
              </>
            ) : (
              item.label
            );

            return (
              <motion.li
                key={key}
                layout
                variants={itemVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.18, ease: subtleEase }}
                className="flex items-center gap-1"
              >
                {index > 0 || hasHome ? (
                  <Icon
                    name="chevron-right"
                    className="mx-0.5 h-3.5 w-3.5 shrink-0 text-[var(--text-muted)] rtl:rotate-180"
                  />
                ) : null}

                {isInteractive ? (
                  <Link
                    to={item.to as string}
                    className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-sm font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--field-focus-ring)]"
                  >
                    {inner}
                  </Link>
                ) : isLast && asPageHeading ? (
                  <h1
                    aria-current="page"
                    className="inline-flex items-center gap-1.5 px-1.5 text-base font-semibold tracking-tight text-[var(--text-primary)]"
                  >
                    {inner}
                  </h1>
                ) : (
                  <span
                    aria-current={isLast ? 'page' : undefined}
                    className={cn(
                      'inline-flex items-center gap-1.5 px-1.5 text-sm font-medium',
                      isLast ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]',
                    )}
                  >
                    {inner}
                  </span>
                )}
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ol>
    </nav>
  );
}
